import List from '@pelckmans/elementis/components/list';
import { useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MedialinkReviewState, type Medialink } from '../../../../api/medialink';
import { AnalyticsEventSubLocation } from '../../../../constants/analyticsEvents';
import { buildGroupProps } from '../../../../utils/elementisUtils';
import ModuleContext from '../../_contexts/ModuleContext';
import MedialinkItem from '../../components/MedialinkItem';
import MedialinksContext from '../_contexts/MedialinksContext';
import SelectionContext from '../_contexts/SelectionContext';
import TableOfContentsContext from '../_contexts/TableOfContentsContext';
import MaterialListHeader from './MaterialListHeader';
import MaterialPills from './MaterialPills';

type MedialinkListProps = {
  medialinks: Medialink[];
  title: string;
  subLocationId: AnalyticsEventSubLocation;
};

export default function MedialinkList({ medialinks, title, subLocationId }: MedialinkListProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedModule } = useContext(ModuleContext);
  const { selectedNode } = useContext(TableOfContentsContext);
  const { showSelection, selectedMedialinks, addMedialinks, removeMedialinks } = useContext(SelectionContext);
  const { fetchMedialinks } = useContext(MedialinksContext);

  const openMedialinkId = searchParams.get('medialinkId');

  function onMedialinkIsOpened() {
    if (!openMedialinkId) return;

    setSearchParams(prev => {
      prev.delete('medialinkId');
      return prev;
    });
  }

  if (!selectedModule) return null;

  function getPillGroup(
    medialink: Medialink,
    isTeacherAlike: boolean,
  ): { 'aria-label': undefined; children: NonNullable<React.ReactNode>; role: undefined } | undefined {
    return isTeacherAlike
      ? buildGroupProps(
          <MaterialPills
            isAssignment={medialink.assignments.length > 0}
            isShared={medialink.shares.length > 0}
            isStudent={medialink.group === 'student'}
            isTeacher={medialink.group === 'teacher'}
            isAdaptive={medialink.assessmentQExercise?.isAdaptive}
            isOnTheFly={medialink.assessmentQExercise?.isOnTheFly}
            isInReview={medialink.reviewState === MedialinkReviewState.IN_REVIEW}
          />,
        )
      : buildGroupProps(
          <MaterialPills
            isAdaptive={medialink.assessmentQExercise?.isAdaptive}
            isOnTheFly={medialink.assessmentQExercise?.isOnTheFly}
          />,
        );
  }

  return (
    <List>
      <MaterialListHeader<Medialink>
        title={title}
        showSelection={showSelection}
        selectedMaterial={selectedMedialinks}
        material={medialinks}
        addMaterial={addMedialinks}
        removeMaterial={removeMedialinks}
      />
      {medialinks.map(medialink => (
        <MedialinkItem
          key={medialink.id}
          node={selectedNode}
          module={selectedModule}
          medialink={medialink}
          canBePinned={true}
          subLocationId={subLocationId}
          refetch={() => fetchMedialinks(false)}
          getPillGroup={getPillGroup}
          openMedialinkId={openMedialinkId}
          onMedialinkIsOpened={onMedialinkIsOpened}
        />
      ))}
    </List>
  );
}
